import { useCallback, useLayoutEffect, useRef } from 'react';
import { AuctionTable } from '../problems/AuctionTable';
import { ScrollMoreHint } from '../common/ScrollMoreHint';
import { useScrollIndicator } from '../../hooks/useScrollIndicator';
import { isSeat } from './SeatLabel';
import type { Position } from '../../types/problems';

/**
 * The centre panel of the table: the auction so far, scrolling inside its own panel (with the
 * scroll-more chevron as the hint) so a long auction never makes the PAGE scroll — the same
 * containment /problems uses.
 *
 * Identical in every table session, so it owns its own scroll rig rather than asking the page
 * to thread refs through: the indicator's callback ref and the DOM ref it needs to rewind are
 * merged here, out of the caller's way.
 */
export function AuctionPanel({
  auction,
  vulnerability,
  decisionSeat,
  pendingSeat = decisionSeat,
  rewindOn,
  bidCommentResolver,
  callFormatter,
  showCallAnnotations = false,
}: {
  auction: { seat: string; call: string; alert?: string | null; comment?: string | null }[];
  vulnerability: string;
  decisionSeat: string;
  /**
   * Seat to mark with the "?" that shows where in the auction the question sits. Defaults to the
   * decision seat, which is what a session wants when the auction it receives stops short of the
   * question.
   *
   * Pass `null` when the auction data ALREADY carries its own mask: `/problems`' 3rd-level mode is
   * masked server-side (`ThirdLevelBidDecisionService.Mask` appends a synthetic "?" at Jeff's
   * seat), so deriving a second one here would render two. Which of those two shapes a caller
   * receives is a fact about its endpoint, not a preference — hence a prop rather than a default
   * this panel could pick for everyone.
   */
  pendingSeat?: string | null;
  /**
   * Rewind the auction to its first bid whenever any of these change — a new hand, or a verdict
   * appearing, so the learner reads the history from the top (mirrors ProblemsPage).
   *
   * An array rather than a single key because the two triggers are independent and a multi-step
   * hand changes only the second: its steps share one hand id, and it is the new verdict object
   * that says a step was answered. The array's LENGTH must be constant across renders — React
   * compares the elements, not the array, so passing a fresh array each render is correct.
   */
  rewindOn: readonly unknown[];
  /**
   * Optional annotation and per-call renderers, passed straight through to `AuctionTable` (see its
   * props for what each does and why it exists). Both are facts about the caller's DATA rather than
   * its taste: a set holding lowercase-`m` minor placeholders, or either/or calls such as `4c/4d`,
   * supplies them; every other caller omits both and gets the table's own defaults, which is what
   * keeps the practice pages' auctions byte-identical.
   */
  bidCommentResolver?: (call: string) => string | null;
  callFormatter?: (call: string) => JSX.Element | null;
  /**
   * Thread each call's AUTHORED annotation (`comment`) down to the table, so an annotated call
   * gets the shared yellow alert cell and its tooltip.
   *
   * <p>Off by default, and deliberately so. This panel has dropped `comment` since it was
   * extracted in feature 023 — it forwards `alert` and nothing else — so every page it serves has
   * been drawing un-annotated cells for as long as it has existed. Turning that on for everyone
   * would light up cells across Bidding Practice and the counting pages in the same commit as an
   * unrelated feature, which is not a change to make by side effect: bid problems carry per-call
   * comments too ('strong', 'showing 22-24', 'prec.'), and whether those should now appear is a
   * question for whoever owns those pages.
   *
   * <p>Feature 029's conceptual page is the one caller that needs it, because the authoring schema
   * REQUIRES an annotation on a wildcard — the annotation is the only thing that says what the
   * wildcard stands for, so a dropped one leaves the reader with a bare `*` and no way to find out
   * (FR-017a). Opt-in keeps that need from reaching any other page (FR-017d).</p>
   */
  showCallAnnotations?: boolean;
}) {
  const { ref: scrollCbRef, showHint } = useScrollIndicator();
  const scrollDomRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollDomRef.current = node;
      scrollCbRef(node);
    },
    [scrollCbRef],
  );

  useLayoutEffect(() => {
    const el = scrollDomRef.current;
    if (!el) return;
    el.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, rewindOn);

  return (
    <div ref={scrollRef} className="auction-scroll scrollable">
      <AuctionTable
        auction={auction.map((b) => ({
          seat: b.seat as Position,
          call: b.call,
          // Bid alerts thread straight through to AuctionTable/BidCell, per the
          // constitution's Bid Alert Display standard — never re-derived here.
          alert: b.alert,
          // The authored annotation only when the caller asked for it — see the prop's own note
          // for why every existing caller keeps getting it dropped.
          comment: showCallAnnotations ? b.comment : undefined,
        }))}
        vulnerability={vulnerability}
        decisionSeat={decisionSeat as Position}
        bidCommentResolver={bidCommentResolver}
        callFormatter={callFormatter}
        // Marks where the question sits in the auction. Shown for Yes/No too, where the
        // auction is otherwise empty and gives no clue whose turn it is — and it stays put
        // after the answer, because an opening question reveals no calls at all and dropping
        // the marker left the panel with a header row and nothing under it.
        pendingSeat={isSeat(pendingSeat) ? pendingSeat : null}
        // The table sits on a cream panel, so the default white sticky header would read as
        // a stripe pasted over the felt theme (client decision, 2026-07-21).
        headerBgClass="bg-cream"
      />
      {showHint && <ScrollMoreHint />}
    </div>
  );
}
